// /src/components/admin/FeedRankingSection.jsx
// Location Depth → Feed ranking (Roadmap #104, docs/briefs/2026-10-18-feed-dam-ranking-addendum-1.md;
// the event half-life since docs/briefs/2026-10-18-feed-dam-ranking-phase2-events.md § Settings):
// per country, the share of priority a News item or an event keeps for each step up or down the
// boundary hierarchy (vertical) and each step to a neighbouring area (horizontal), News's half-life
// by age, and Events' look-ahead half-life by nearness of start. The country's time zone is shown,
// read-only (it is set in the database). Saving takes effect on the next feed load (no cache,
// addendum 2 ruling 4). Platform admins only: both functions check is_platform_admin(), and the
// database logs each save. English only, no translation keys: the Admin Panel ruling (Michael,
// 3 Oct 2026).

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import StandardButton from '../core/StandardButton';
import { getFeedRankingSettings, saveFeedRankingSettings } from '../../data/feedRankingService';

const SHARE_MIN = 1, SHARE_MAX = 100, HALF_LIFE_MIN = 1, HALF_LIFE_MAX = 8760;

const toDraft = (row) => ({
  vertical: String(row.keep_vertical_pct),
  horizontal: String(row.keep_horizontal_pct),
  halfLife: String(row.half_life_hours),
  eventHalfLife: String(row.event_half_life_hours)
});

// "1 hour", "72 hours"; a whole number of days reads as days: "7 days", "1 day".
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const asPeriod = (hours) => (hours % 24 === 0 ? plural(hours / 24, 'day', 'days') : plural(hours, 'hour', 'hours'));

// What is wrong with a draft, in plain words, or null when it can be saved.
const validate = (draft) => {
  const v = Number(draft.vertical), h = Number(draft.horizontal), hl = Number(draft.halfLife);
  const ehl = Number(draft.eventHalfLife);
  if ([draft.vertical, draft.horizontal, draft.halfLife, draft.eventHalfLife].some(s => s.trim() === '')
      || [v, h, hl, ehl].some(Number.isNaN)) {
    return 'Every field needs a number.';
  }
  if (v < SHARE_MIN || v > SHARE_MAX || h < SHARE_MIN || h > SHARE_MAX) {
    return 'Each share is from 1% to 100%. 0% would close the feed.';
  }
  if (hl < HALF_LIFE_MIN || hl > HALF_LIFE_MAX) {
    return 'The half-life is from 1 to 8,760 hours.';
  }
  if (ehl < HALF_LIFE_MIN || ehl > HALF_LIFE_MAX) {
    return 'The event look-ahead half-life is from 1 to 8,760 hours.';
  }
  return null;
};

// The example line under the fields, from the figures as entered.
const exampleLine = (draft) => {
  if (validate(draft)) return null;
  const v = Number(draft.vertical) / 100, h = Number(draft.horizontal) / 100;
  const pct = (x) => `${Math.round(x * 1000) / 10}%`;
  return `A district item reaches a parish reader at ${pct(v * v)} of its priority, and an item from a ` +
    `neighbouring parish at ${pct(h)}; these shares apply to News and Events alike. A News item loses half ` +
    `its priority for each ${asPeriod(Number(draft.halfLife))} of age. An event loses half its priority for ` +
    `each ${asPeriod(Number(draft.eventHalfLife))} until it starts.`;
};

// The database's refusals start with a code, then a plain sentence: show the sentence.
const plainError = (message) => {
  const i = message.indexOf(': ');
  return i > 0 && /^[a-z_]+$/.test(message.slice(0, i)) ? message.slice(i + 2) : message;
};

const FeedRankingSection = () => {
  const theme = useTheme();
  const { formattingLocale } = useTranslation('admin');
  const [state, setState] = useState({ loading: true, error: null, rows: [] });
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [messages, setMessages] = useState({});

  const load = async () => {
    const { rows, error } = await getFeedRankingSettings();
    setState({ loading: false, error: error || null, rows });
    setDrafts(Object.fromEntries(rows.map(row => [row.country_code, toDraft(row)])));
  };

  useEffect(() => { load(); }, []);

  const setField = (country, field, value) => {
    setDrafts(prev => ({ ...prev, [country]: { ...prev[country], [field]: value } }));
    setMessages(prev => ({ ...prev, [country]: null }));
  };

  const handleSave = async (country) => {
    const draft = drafts[country];
    const problem = validate(draft);
    if (problem) {
      setMessages(prev => ({ ...prev, [country]: { error: true, text: problem } }));
      return;
    }
    setSaving(country);
    const { error } = await saveFeedRankingSettings(country, Number(draft.vertical), Number(draft.horizontal),
      Number(draft.halfLife), Number(draft.eventHalfLife));
    setSaving(null);
    if (error) {
      setMessages(prev => ({ ...prev, [country]: { error: true, text: plainError(error) } }));
      return;
    }
    setMessages(prev => ({ ...prev, [country]: { error: false, text: 'Saved. The next feed load uses the new figures.' } }));
    await load();
  };

  const formatChanged = (row) => {
    if (!row.updated_by_name) return 'Not changed since it was set up.';
    const when = new Date(row.updated_at).toLocaleString(formattingLocale, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    return `Last changed ${when} by ${row.updated_by_name}.`;
  };

  const box = {
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px'
  };
  const label = { display: 'block', fontSize: '13px', color: theme.text.secondary, marginBottom: '4px' };
  const input = {
    width: '100px',
    padding: '8px',
    border: `1px solid ${tokens.colors.disabledBackground}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'OpenSauceSans'
  };

  return (
    <div style={box}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: theme.text.primary }}>
        Feed ranking
      </h3>
      <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: theme.text.secondary, lineHeight: '1.5' }}>
        How much priority a News item or an event keeps for each boundary it crosses on its way to
        the reader. A vertical step is up or down the hierarchy (parish, municipality, district); a
        horizontal step is to a neighbouring area. Nothing is closed: distant items appear below
        local ones.
      </p>

      {state.loading && <p style={{ color: tokens.colors.textSecondary }}>Loading feed ranking settings…</p>}
      {state.error && <p style={{ color: tokens.colors.destructive }}>Could not load the feed ranking settings.</p>}

      {state.rows.map(row => {
        const country = row.country_code;
        const draft = drafts[country] || toDraft(row);
        const message = messages[country];
        const example = exampleLine(draft);
        return (
          <div key={country} style={{ borderTop: `1px solid ${tokens.colors.disabledBackground}`, padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ width: '40px', fontSize: '16px', fontWeight: '600', color: theme.text.primary, paddingBottom: '8px' }}>
                {country}
              </div>
              <div>
                <label style={label} htmlFor={`feed-v-${country}`}>Vertical step (%)</label>
                <input id={`feed-v-${country}`} type="number" min={SHARE_MIN} max={SHARE_MAX} step="1" style={input}
                  value={draft.vertical} onChange={e => setField(country, 'vertical', e.target.value)} />
              </div>
              <div>
                <label style={label} htmlFor={`feed-h-${country}`}>Horizontal step (%)</label>
                <input id={`feed-h-${country}`} type="number" min={SHARE_MIN} max={SHARE_MAX} step="1" style={input}
                  value={draft.horizontal} onChange={e => setField(country, 'horizontal', e.target.value)} />
              </div>
              <div>
                <label style={label} htmlFor={`feed-hl-${country}`}>Half-life (hours)</label>
                <input id={`feed-hl-${country}`} type="number" min={HALF_LIFE_MIN} max={HALF_LIFE_MAX} step="1" style={input}
                  value={draft.halfLife} onChange={e => setField(country, 'halfLife', e.target.value)} />
              </div>
              <div>
                <label style={label} htmlFor={`feed-ehl-${country}`}>Event look-ahead half-life (hours)</label>
                <input id={`feed-ehl-${country}`} type="number" min={HALF_LIFE_MIN} max={HALF_LIFE_MAX} step="1" style={input}
                  value={draft.eventHalfLife} onChange={e => setField(country, 'eventHalfLife', e.target.value)} />
              </div>
              <StandardButton
                variant="action"
                label={saving === country ? 'Saving…' : 'Save'}
                disabled={saving !== null}
                onClick={() => handleSave(country)}
              />
            </div>
            {example && (
              <p style={{ margin: '10px 0 0 56px', fontSize: '13px', color: theme.text.primary }}>{example}</p>
            )}
            <p style={{ margin: '4px 0 0 56px', fontSize: '12px', color: theme.text.secondary }}>
              {row.timezone ? `Time zone: ${row.timezone} (set in the database, not here). ` : ''}{formatChanged(row)}
            </p>
            {message && (
              <p style={{ margin: '6px 0 0 56px', fontSize: '13px', color: message.error ? tokens.colors.destructive : tokens.colors.actionDiluteText }}>
                {message.text}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FeedRankingSection;
